import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Chart, ArcElement, DoughnutController, Tooltip } from "chart.js";

Chart.register(ArcElement, DoughnutController, Tooltip);

export default function ScanCharts({
    title,
    subtitle,
    data,
    centerValue,
    centerLabel,
    onViewReport,
    onChartClick,
    reportLoading = false,
    compact = false,
    emptyMessage = "No data to display",
}) {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    const chartData = useMemo(() => {
        if (!Array.isArray(data)) return [];

        const entries = data.filter(
            (entry) =>
                entry != null &&
                typeof entry.name === "string" &&
                Number.isFinite(entry.value) &&
                entry.value > 0
        );

        const total = entries.reduce((sum, entry) => sum + entry.value, 0);

        return entries.map((entry) => ({
            ...entry,
            percent: Number.isFinite(entry.percent)
                ? entry.percent
                : Number(((entry.value / total) * 100).toFixed(1)),
        }));
    }, [data]);

    const hasData = chartData.length > 0;
    const canViewReport = typeof onViewReport === "function";

    useEffect(() => {
        if (!canvasRef.current || !hasData) return;

        const chart = new Chart(canvasRef.current, {
            type: "doughnut",
            data: {
                labels: chartData.map((entry) => t(entry.name)),
                datasets: [
                    {
                        data: chartData.map((entry) => entry.value),
                        backgroundColor: chartData.map((entry) => entry.color),
                        borderWidth: 0,
                        cutout: compact ? "78%" : "70%",
                        spacing: compact ? 4 : 0,
                        borderRadius: compact ? 6 : 0,
                        hoverOffset: 0,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,

                onClick: (event, elements) => {
                    if (elements && elements.length > 0 && onChartClick) {
                        const dataIndex = elements[0].index;
                        const clickedItem = chartData[dataIndex];
                        onChartClick(clickedItem);
                    }
                },

                onHover: (event, elements) => {
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = elements && elements.length > 0 ? 'pointer' : 'default';
                    }
                },

                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const entry = chartData[context.dataIndex];
                                return `${context.label}: ${entry.value} (${entry.percent}%)`;
                            },
                        },
                    },
                },
            },
        });

        chartRef.current = chart;

        return () => {
            chart.destroy();
            if (chartRef.current === chart) {
                chartRef.current = null;
            }
        };
    }, [chartData, hasData, t, compact]);

    const handleLegendClick = (entry) => {
        if (onChartClick) {
            onChartClick(entry);
        }
    };

    return (
        <div
            className={`scan-charts-content ${
                compact ? "scan-charts-content--compact" : ""
            }`}
        >
            <div className="scan-charts-content-header">
                <div className="scan-charts-content-header-label">
                    <h3 className="scan-charts-content-header-label-title">
                        {t(title)}
                    </h3>

                    {subtitle && (
                        <p className="scan-charts-content-header-label-subtitle">
                            {t(subtitle)}
                        </p>
                    )}
                </div>

                {canViewReport && (
                    <button
                        type="button"
                        className="scan-charts-content-header-button"
                        onClick={onViewReport}
                        disabled={reportLoading}
                        aria-busy={reportLoading}
                    >
                        {t("View report")}
                    </button>
                )}
            </div>

            <div className="scan-charts-container">
                {hasData ? (
                    <>
                        <div className="scan-charts-visual">
                            <canvas
                                ref={canvasRef}
                                role="img"
                                aria-label={`${t(title)}: ${centerValue ?? "—"} ${
                                    centerLabel ? t(centerLabel) : ""
                                }`}
                            />

                            <div className="scan-charts-center">
                                <span className="scan-charts-center-value">
                                    {centerValue ?? "—"}
                                </span>

                                {centerLabel && (
                                    <span className="scan-charts-center-label">
                                        {t(centerLabel)}
                                    </span>
                                )}
                            </div>
                        </div>

                        <ul className="scan-charts-legend">
                            {chartData.map((entry) => (
                                <li
                                    key={entry.name}
                                    className="scan-charts-legend-item"
                                    title={`${t(entry.name)}: ${entry.value} (${entry.percent}%)`}
                                    onClick={() => handleLegendClick(entry)}
                                    style={{ cursor: onChartClick ? 'pointer' : 'default' }}
                                >
                                    <div className="scan-charts-legend-item-left">
                                        <span
                                            className="scan-charts-legend-dot"
                                            style={{ background: entry.color }}
                                            aria-hidden="true"
                                        />

                                        <span className="scan-charts-legend-name">
                                            {t(entry.name)}
                                        </span>
                                    </div>

                                    <span className="scan-charts-legend-value">
                                        {entry.percent}%
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </>
                ) : (
                    <p
                        role="status"
                        style={{
                            width: "100%",
                            margin: 0,
                            fontSize: 12,
                            lineHeight: 1.5,
                            textAlign: "center",
                            color: "#8a8fa3",
                        }}
                    >
                        {t(emptyMessage)}
                    </p>
                )}
            </div>
        </div>
    );
}